import { Buttons } from "@neon-purge/shared";

/** Rebindable action → KeyboardEvent.code. Persisted in localStorage. */
export type Action =
  | "forward" | "back" | "left" | "right"
  | "jump" | "crouch" | "sprint"
  | "reload" | "use" | "melee" | "grenade"
  | "grapple" | "stoke"
  | "scoreboard" | "thirdPerson";

export const DEFAULT_BINDINGS: Record<Action, string[]> = {
  forward: ["KeyW"],
  back: ["KeyS"],
  left: ["KeyA"],
  right: ["KeyD"],
  jump: ["Space"],
  crouch: ["KeyC"],
  // sprint on SHIFT — Ctrl triggered Chrome shortcuts (Ctrl+W closed the tab!)
  sprint: ["ShiftLeft", "ShiftRight"],
  reload: ["KeyR"],
  use: ["KeyF"],
  // quick melee stab on ALT (also bound to middle-mouse in code) — no weapon swap
  melee: ["AltLeft", "AltRight"],
  // throw a grenade
  grenade: ["KeyE"],
  // GRAPPLING HOOK — physical Q key: Q on QWERTY, A on AZERTY.
  grapple: ["KeyQ"],
  // BURNOUT STOKE — light your own fuse (wager). Inert in every other mode.
  stoke: ["KeyG"],
  scoreboard: ["Tab"],
  // HOLD to pull the camera back to third-person (also D-pad → on a pad). V is
  // the same physical key on QWERTY and AZERTY.
  thirdPerson: ["KeyV"],
};

const BINDINGS_KEY = "neonpurge.bindings";
const SENS_KEY = "neonpurge.sensitivity";
const FAVEMOTE_KEY = "neonpurge.favEmote";

/** Snap any value to a valid taunt kind (0..4); anything else → 0 (wave). The
 *  emote wheel, the persisted favourite, and the server all share this range. */
export function clampEmoteKind(n: unknown): number {
  const k = Math.floor(Number(n));
  return Number.isFinite(k) && k >= 0 && k <= 4 ? k : 0;
}

/** The player's favourite emote — the quick-tap default, persisted per device so
 *  a chosen main taunt survives across matches (was in-memory, reset to wave). */
export function loadFavEmote(): number {
  try { return clampEmoteKind(localStorage.getItem(FAVEMOTE_KEY)); } catch { return 0; }
}
export function saveFavEmote(kind: number): void {
  try { localStorage.setItem(FAVEMOTE_KEY, String(clampEmoteKind(kind))); } catch { /* private mode */ }
}

export function loadBindings(): Record<Action, string[]> {
  try {
    const raw = localStorage.getItem(BINDINGS_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<Record<Action, string[]>>;
      const merged = { ...DEFAULT_BINDINGS, ...saved };
      if (
        !saved.grapple ||
        (saved.grapple.length === 1 && saved.grapple[0] === "KeyG") ||
        (saved.grapple.includes("KeyQ") && saved.grapple.includes("KeyA"))
      ) {
        merged.grapple = DEFAULT_BINDINGS.grapple;
      }
      return merged;
    }
  } catch { /* fall through to defaults */ }
  return { ...DEFAULT_BINDINGS };
}

export function saveBindings(b: Record<Action, string[]>): void {
  localStorage.setItem(BINDINGS_KEY, JSON.stringify(b));
}

export interface Sensitivity {
  mouse: number;
  adsMult: number;
  invertY: boolean;
  /** Touch drag-look multiplier (phone) — own dial, independent of mouse. */
  touchMul: number;
}

export function loadSensitivity(): Sensitivity {
  try {
    const raw = localStorage.getItem(SENS_KEY);
    if (raw) return { mouse: 0.0023, adsMult: 0.7, invertY: false, touchMul: 1, ...(JSON.parse(raw) as Partial<Sensitivity>) };
  } catch { /* defaults */ }
  return { mouse: 0.0023, adsMult: 0.7, invertY: false, touchMul: 1 };
}

export function saveSensitivity(s: Sensitivity): void {
  localStorage.setItem(SENS_KEY, JSON.stringify(s));
}

export interface Behavior {
  adsHold: boolean;
  crouchHold: boolean;
  fov: number;
  /** Aim assist — OFF by default; persisted so it stays set per device. */
  autoAim: boolean;
  /** Controller vibration strength multiplier (0 = off … 2.5 = heavy). */
  rumble: number;
  /** Recorded voice announcer on/off. */
  voiceAnnounce: boolean;
  /** Mobile: show the on-screen touch buttons/joystick. ON by default; turn
   *  off to play with a controller and clear the glass of overlay buttons. */
  onScreenButtons: boolean;
  /** Crosshair customization (device-global, applied to :root CSS vars by
   *  applyCrosshairSettings). All optional so pre-existing saved behavior
   *  JSON keeps working — the resolver fills defaults that reproduce the
   *  original neon reticle exactly. */
  chStyle?: "neon" | "cross" | "dot" | "crossdot";
  /** Palette key resolved in hud.ts CH_COLORS (cyan/green/white/red/…). */
  chColor?: string;
  /** Arm length in px. */
  chSize?: number;
  /** Arm thickness in px. */
  chThick?: number;
  /** Resting gap from centre in px (reactive spread adds on top). */
  chGap?: number;
  /** Dark outline for contrast against bright surfaces. */
  chOutline?: boolean;
  /** Colour-blind marker palette (compass / minimap / legend). */
  colorblind?: "off" | "deut" | "prot" | "trit";
  /** Caption every recorded announcer line. ON unless turned off. */
  subtitles?: boolean;
  /** Show one-time contextual tips + the mode card. ON unless turned off. */
  tips?: boolean;
}

/** Crosshair defaults — reproduce the original neon reticle exactly. Shared by
 *  the settings UI (initial slider/select values) and the CSS-var resolver. */
export const CH_DEFAULTS = {
  chStyle: "neon" as NonNullable<Behavior["chStyle"]>,
  chColor: "cyan",
  chSize: 6,
  chThick: 2,
  chGap: 2,
  chOutline: false,
};

const BEHAVIOR_KEY = "neonpurge.behavior";

/** RIGHT-STICK autodetect: pad layouts disagree about which axes carry the
 *  right stick (standard: 2/3; DirectInput DS4: 3/4; others: 4/5 with L2/R2
 *  in between resting at ±1). Baseline each pad's axes the first time we see
 *  it (sticks assumed neutral), then the two lowest-index NON-TRIGGER axes
 *  after the left stick are X and Y. */
const padAxisRest = new Map<number, number[]>();
export function rightStick(gp: Gamepad): { x: number; y: number } {
  // Browsers normalize standard-mapped pads to the canonical 0/1 + 2/3
  // layout. Do not baseline these axes: the first Gamepad API exposure often
  // happens because the user moved a stick, which would otherwise calibrate
  // that deliberate movement away until reconnect.
  if (gp.mapping === "standard") return { x: gp.axes[2] ?? 0, y: gp.axes[3] ?? 0 };
  let rest = padAxisRest.get(gp.index);
  if (!rest || rest.length !== gp.axes.length) {
    rest = [...gp.axes];
    padAxisRest.set(gp.index, rest);
  }
  const cands: number[] = [];
  for (let i = 2; i < Math.min(8, gp.axes.length); i++) {
    if (Math.abs(rest[i]) > 0.5) continue; // triggers rest at ±1, hat ≈3.3
    cands.push(i);
    if (cands.length === 2) break;
  }
  const xi = cands[0] ?? 2, yi = cands[1] ?? 3;
  return { x: (gp.axes[xi] ?? 0) - (rest[xi] ?? 0), y: (gp.axes[yi] ?? 0) - (rest[yi] ?? 0) };
}

// LOCAL SPLIT-SCREEN: pads claimed by extra local players (P2-P4). A claimed
// pad is invisible to activeGamepad() — P1's keyboard/mouse/pad input and the
// menu pad-nav must never read a seat-mate's controller. The claim also mutes
// it for the shared rumble helpers (each seat owns its own motor).
const reservedPads = new Set<number>();
export function claimPad(index: number): void { reservedPads.add(index); }
export function releasePad(index: number): void { reservedPads.delete(index); }
export function releaseAllPads(): void { reservedPads.clear(); }
export function isPadReserved(index: number): boolean { return reservedPads.has(index); }

// Desktop controller tools commonly expose a virtual/ghost pad before the
// physical one. Selecting slot 0 (or the first "standard" mapping) makes the
// real controller appear randomly dead. Keep one app-wide active pad and let
// deliberate input move ownership to whichever connected device is in use.
let activePadIndex = -1;
let activePadId = "";
interface PadIntentState { buttons: boolean[]; axes: boolean[]; lastIntentAt: number }
const padIntent = new Map<string, PadIntentState>();

/** One controller selector shared by menus, gameplay and helper actions. */
export function activeGamepad(): Gamepad | null {
  const pads = navigator.getGamepads?.() ?? [];
  const connected: Gamepad[] = [];
  for (const gp of pads) if (gp?.connected && !reservedPads.has(gp.index)) connected.push(gp);
  if (!connected.length) { activePadIndex = -1; activePadId = ""; return null; }

  const liveKeys = new Set<string>();
  for (const gp of connected) {
    const key = `${gp.index}:${gp.id}`;
    liveKeys.add(key);
    const buttons = gp.buttons.map((b) => b.pressed || b.value > 0.5);
    // Only crossings claim ownership. Constant trigger rest values, stick
    // drift and virtual-pad noise cannot repeatedly steal it back.
    const axes = gp.axes.slice(0, 4).map((v) => Math.abs(v) > 0.5);
    const prev = padIntent.get(key);
    const freshButton = buttons.some((on, i) => on && !prev?.buttons[i]);
    const freshAxis = axes.some((on, i) => on && !prev?.axes[i]);
    padIntent.set(key, {
      buttons,
      axes,
      lastIntentAt: freshButton || freshAxis ? performance.now() : (prev?.lastIntentAt ?? 0),
    });
  }
  for (const key of padIntent.keys()) if (!liveKeys.has(key)) padIntent.delete(key);

  let current = connected.find((gp) => gp.index === activePadIndex && gp.id === activePadId) ?? null;
  let claimant: Gamepad | null = current;
  let claimantAt = current ? (padIntent.get(`${current.index}:${current.id}`)?.lastIntentAt ?? 0) : -1;
  for (const gp of connected) {
    const at = padIntent.get(`${gp.index}:${gp.id}`)?.lastIntentAt ?? 0;
    if (at > claimantAt) {
      claimant = gp;
      claimantAt = at;
    }
  }
  if (claimant) current = claimant;
  if (!current) current = connected.find((gp) => gp.mapping === "standard") ?? connected[0];
  activePadIndex = current.index;
  activePadId = current.id;
  return current;
}

/** SHARED rumble priority: a running strong effect holds the actuator so
 *  weaker calls (footsteps!) can't stomp it — shared between padRumble (menu
 *  launches) and InputManager.rumble (in-game) since they drive one motor. */
let rumbleHoldEndAt = 0;
let rumbleHoldMag = 0;
// ---- rebindable GAMEPAD actions (Settings → Controller) ----
export type PadAction = "fire" | "jump" | "crouch" | "sprint" | "reload" | "use" | "ads" | "grenade" | "melee" | "heal" | "taunt" | "stoke";
export const PAD_BIND_DEFAULTS: Record<PadAction, number> = {
  fire: 7, jump: 0, crouch: 1, sprint: 10, reload: 2, use: 3,
  ads: 6, grenade: 5, melee: 11, heal: 12, taunt: 13, stoke: 14,
};
export const PAD_BUTTON_NAMES: string[] = [
  "✕ Cross", "◯ Circle", "□ Square", "△ Triangle", "L1", "R1", "L2", "R2",
  "Share", "Options", "L3 (stick)", "R3 (stick)", "D-Pad ↑", "D-Pad ↓", "D-Pad ←", "D-Pad →",
];
export function padButtonName(i: number): string { return PAD_BUTTON_NAMES[i] ?? `Button ${i}`; }
const PAD_BINDS_KEY = "neonpurge.padBinds";
export function loadPadBinds(): Record<PadAction, number> {
  try {
    return { ...PAD_BIND_DEFAULTS, ...(JSON.parse(localStorage.getItem(PAD_BINDS_KEY) ?? "{}") as Partial<Record<PadAction, number>>) };
  } catch { return { ...PAD_BIND_DEFAULTS }; }
}
export function savePadBinds(b: Record<PadAction, number>): void {
  try { localStorage.setItem(PAD_BINDS_KEY, JSON.stringify(b)); } catch { /* storage off */ }
  window.dispatchEvent(new Event("np-padbinds")); // live InputManagers re-read
}

export function rumbleGate(mag: number, durMs: number): boolean {
  const now = performance.now();
  if (now < rumbleHoldEndAt && mag < rumbleHoldMag) return false;
  rumbleHoldEndAt = now + durMs;
  rumbleHoldMag = mag;
  return true;
}

/** iOS web-haptic: Safari has NO Vibration API, but toggling a hidden
 *  <input type=checkbox switch> fires a subtle SYSTEM haptic on iOS 17.4+ —
 *  the only browser haptic Apple allows. One fixed-strength tap (no rumble
 *  curve, no duration). Built lazily; silently no-ops on anything older. */
let iosSwitch: HTMLInputElement | null = null;
export function iosHaptic(): void {
  try {
    if (!iosSwitch) {
      const label = document.createElement("label");
      label.setAttribute("aria-hidden", "true");
      label.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.setAttribute("switch", ""); // the magic attribute iOS haptic-taps on
      label.appendChild(input);
      document.body.appendChild(label);
      iosSwitch = input;
    }
    iosSwitch.click(); // toggling the switch is what fires the system tap
  } catch { /* unsupported browser */ }
}

function deviceHaptic(strong: number, weak: number, durMs: number): void {
  const mag = Math.min(1, strong * 1.3 + weak * 0.5);
  if (mag <= 0.02) return;
  if (typeof navigator.vibrate === "function") {
    const vibMs = Math.round(Math.min(durMs, 18 + mag * 200));
    try { navigator.vibrate(vibMs); } catch { /* refused */ }
  } else if (mag > 0.12) {
    iosHaptic();
  }
}

/** One-shot rumble from ANY UI code (menu launches etc.) — first pad with an
 *  actuator, scaled by the Vibration setting. */
export function padRumble(durationMs: number, weak: number, strong: number): void {
  const mult = loadBehavior().rumble ?? 1;
  if (mult <= 0) return;
  if (!rumbleGate(weak + strong, durationMs)) return;
  let rumbled = false;
  for (const gp of navigator.getGamepads?.() ?? []) {
    if (gp && reservedPads.has(gp.index)) continue; // seat-mates' pads: theirs, not ours
    const act = (gp as unknown as { vibrationActuator?: { playEffect?: (t: string, o: object) => Promise<unknown> | void } } | null)?.vibrationActuator;
    if (!gp || !act?.playEffect) continue;
    try {
      const p = act.playEffect("dual-rumble", { duration: durationMs, weakMagnitude: Math.min(1, weak * mult), strongMagnitude: Math.min(1, strong * mult) });
      void Promise.resolve(p).catch(() => deviceHaptic(strong * mult, weak * mult, durationMs));
      rumbled = true;
    } catch { /* refused — fallback below */ }
    break;
  }
  if (!rumbled) deviceHaptic(strong * mult, weak * mult, durationMs);
}

export function loadBehavior(): Behavior {
  try {
    const raw = localStorage.getItem(BEHAVIOR_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<Behavior> & { crouchV2?: boolean };
      const b = { adsHold: true, crouchHold: false, fov: 90, autoAim: false, rumble: 1, voiceAnnounce: true, onScreenButtons: true, ...saved };
      // one-time migration: crouch used to DEFAULT to hold and got persisted
      // alongside unrelated settings — force everyone onto toggle once, then
      // respect whatever they set in the settings screen from here on.
      if (!saved.crouchV2) {
        b.crouchHold = false;
        localStorage.setItem(BEHAVIOR_KEY, JSON.stringify({ ...b, crouchV2: true }));
      }
      return b;
    }
  } catch { /* defaults */ }
  // crouch defaults to TOGGLE (press to stay bent, press again to stand)
  return { adsHold: true, crouchHold: false, fov: 90, autoAim: false, rumble: 1, voiceAnnounce: true, onScreenButtons: true };
}

export function saveBehavior(b: Behavior): void {
  localStorage.setItem(BEHAVIOR_KEY, JSON.stringify({ ...b, crouchV2: true }));
}

const PITCH_LIMIT = Math.PI / 2 - 0.01;

/**
 * Captures keyboard + mouse while pointer lock is held and produces the
 * (moveX, moveZ, yaw, pitch, buttons) tuple the netcode sends as PlayerInput.
 */
export class InputManager {
  yaw = 0;
  pitch = 0;
  private keys = new Set<string>();
  private mouseButtons = 0;
  private bindings = loadBindings();
  sens = loadSensitivity();
  behavior = loadBehavior();
  /** Edge-triggered action queue (fire/melee taps between frames). */
  private tapped = new Set<Action | "fire">();
  adsHeld = false;
  private adsToggled = false;
  private crouchToggled = false;
  /** One-frame quick-melee request (from Alt key or middle-mouse). */
  private meleeTap = false;
  /** One-frame grenade-throw request (from E key). */
  private grenadeTap = false;
  /** One-frame stored heal request (D-pad up / ArrowUp). */
  private healTap = false;
  /** One-frame taunt request (D-pad down / ArrowDown). */
  private tauntTap = false;
  /** One-frame BURNOUT STOKE request (G key / D-pad left / HUD chip tap). */
  private stokeTap = false;

  // ---- EMOTE WHEEL -----------------------------------------------------------
  // HOLD ArrowDown to open a radial taunt picker; the mouse steers the highlight
  // (look is frozen while open — onMouseMove routes here instead of the camera),
  // release commits the highlighted emote. A quick press/release with no drag
  // repeats the last pick, so a fast tap still feels like the old one-button
  // taunt. Gamepad keeps the D-pad-down random taunt (readPad's tauntTap).
  /** Number of taunts on the wheel (kinds 0..4, matches the server + avatar). */
  readonly emoteCount = 5;
  /** True while the wheel is up — match.ts shows the overlay and freezes look. */
  emoteWheelOpen = false;
  /** Highlighted segment while open (-1 = centre deadzone / no selection). */
  emoteSel = -1;
  /** Accumulated pointer drift while the wheel is open (px, clamped). */
  private emoteVecX = 0;
  private emoteVecY = 0;
  /** One-shot committed pick; read + cleared by consumeEmotePick() (-1 = none). */
  private emotePick = -1;
  /** Last emote chosen — the quick-tap default (repeat instead of rolling).
   *  Seeded from the persisted favourite so it survives across matches. */
  private lastEmoteKind = loadFavEmote();
  /** The quick-tap default emote — the HUD marks this tile on the wheel. */
  get favEmote(): number { return this.lastEmoteKind; }

  /** Arena kart THROTTLE (Mario-Kart): the kart only moves when you press
   *  accelerate — X/Cross (0) or the W key = FORWARD; Triangle (3) or
   *  S = REVERSE. The stick front/back does NOTHING (steering only), so the kart
   *  never creeps. Returns -1 | 0 | 1. */
  /** The pad every helper reads: standard-mapping first (same rule as
   *  updateGamepad — the kart helpers used to take the FIRST connected pad,
   *  so a Steam Input ghost pad in slot 0 killed throttle/throw/drift while
   *  look/fire worked), cached ~1 frame (getGamepads() allocates per call). */
  private cachedPad: Gamepad | null = null;
  private cachedPadAt = 0;
  private activePad(): Gamepad | null {
    const now = performance.now();
    if (now - this.cachedPadAt < 12) return this.cachedPad;
    this.cachedPadAt = now;
    this.cachedPad = activeGamepad();
    return this.cachedPad;
  }

  kartThrottle(): number {
    const fwdKey = this.bindings.forward.some((c) => this.keys.has(c));
    const backKey = this.bindings.back.some((c) => this.keys.has(c));
    const gp = this.activePad();
    const padFwd = gp?.buttons[0]?.pressed ?? false; // X / Cross only
    const padBack = gp?.buttons[3]?.pressed ?? false; // Triangle
    if (fwdKey || padFwd || this.touchMoveZ > 0.3) return 1;
    if (backKey || padBack || this.touchMoveZ < -0.3) return -1;
    return 0;
  }

  /** Arena: left stick pushed DOWN (bottom) — the drop-behind modifier when
   *  throwing. (Front/back otherwise does nothing; this is the only stick-Y use.) */
  kartStickDown(): boolean {
    const gp = this.activePad();
    return gp ? (gp.axes[1] ?? 0) > 0.5 : false; // left-stick Y, positive = down
  }

  /** Arena keyboard/mouse: RIGHT-CLICK = throw the bomb BEHIND (drop trap). */
  kartRightClick(): boolean {
    return (this.mouseButtons & 4) !== 0; // right button = bit 2
  }

  /** Arena hop — Space or R1/RB. This must never spend a bomb. */
  kartHopHeld(): boolean {
    if (this.bindings.jump.some((c) => this.keys.has(c))) return true; // Space
    if ((this.touchButtons & Buttons.Jump) !== 0) return true; // touch ⤒
    return this.activePad()?.buttons[5]?.pressed ?? false; // R1/RB
  }

  /** Arena THROW — L2/LT or left-click only.
   *  Deliberately EXCLUDES R1/RB, Space, X/Cross and RT/R2, so hop, gas and
   *  drift never throw a bomb. */
  kartThrowPressed(): boolean {
    if ((this.mouseButtons & 1) !== 0) return true;                    // left-click
    if ((this.touchButtons & Buttons.Fire) !== 0) return true;         // touch 🔥
    const b = this.activePad()?.buttons[6];                            // L2/LT
    return (b?.value ?? 0) > 0.3 || (b?.pressed ?? false);
  }

  /** Arena drift: RT/R2 on gamepad, Shift on keyboard. In Arena the match loop
   *  clears normal crouch/free-look meanings while driving, so Shift is free
   *  to behave like Mario-Kart drift. */
  kartDriftHeld(): boolean {
    if (this.keys.has("ShiftLeft") || this.keys.has("ShiftRight")) return true;
    const b = this.activePad()?.buttons[7]; // RT/R2
    return (b?.value ?? 0) > 0.3 || (b?.pressed ?? false);
  }

  /** FPS-style haptics: dual-rumble on the active pad. `strong` drives the
   *  low-frequency motor (impacts), `weak` the high-frequency one (texture).
   *  Fire = light tick, hits = thump, deaths/explosions = full kick. */
  /** A strong effect HOLDS the actuator: weaker calls can't stomp it mid-run
   *  (footsteps fired 25ms micro-rumbles that replaced the 2s imperion slam
   *  within milliseconds). Also: duration used to be silently capped at 1s —
   *  every "2 second" rumble was actually one. Hold state is SHARED with
   *  padRumble so the menu launch blast survives into the match. */
  rumble(strong: number, weak: number, ms: number): void {
    const mul = this.behavior.rumble ?? 1;
    if (mul <= 0) return; // vibration disabled in settings
    const dur = Math.max(10, Math.min(3000, ms));
    if (!rumbleGate(strong + weak, dur)) return; // a bigger effect is running
    strong *= mul; weak *= mul;
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    let padRumbled = false;
    for (const gp of pads) {
      if (!gp?.connected || reservedPads.has(gp.index)) continue;
      const act = (gp as unknown as { vibrationActuator?: { playEffect?: (t: string, p: object) => Promise<unknown> } }).vibrationActuator;
      // ghost/virtual pads (Steam Input etc.) can occupy slot 0 while
      // CONNECTED but with no actuator — keep looking for one that rumbles
      if (!act?.playEffect) continue;
      const effect = act.playEffect("dual-rumble", {
        duration: dur,
        strongMagnitude: Math.max(0, Math.min(1, strong)),
        weakMagnitude: Math.max(0, Math.min(1, weak)),
      });
      void effect?.catch(() => deviceHaptic(strong, weak, dur));
      padRumbled = true;
      break;
    }
    // PHONE HAPTICS: no gamepad → buzz the device on the SAME events as the
    // controller. Android has the Web Vibration API (magnitude-less, so effect
    // strength maps to buzz LENGTH). iOS Safari has NO vibration API at all —
    // Apple blocks navigator.vibrate — so there we fall back to the hidden
    // <input switch> system-haptic trick (iOS 17.4+), the only web haptic on
    // iOS. It's a single fixed tap (no intensity), fired only for real hits.
    if (!padRumbled) {
      deviceHaptic(strong, weak, dur);
    }
  }
  /** Double-tap-forward run state. */
  private lastFwdTapMs = 0;
  private runMode = false;
  onEscape: (() => void) | null = null;
  /** Fired on the M key press-edge (voice mic toggle). */
  onMicKey: (() => void) | null = null;
  /** Shift held — used for vehicle free-look. */
  get shiftHeld(): boolean { return this.keys.has("ShiftLeft") || this.keys.has("ShiftRight") || this.gpFreeLook; }

  private lastMicTap = 0;

  /** DISMISS (the match-start rules card): set by the match each frame while a
   *  dismissable panel is up. While armed, a fresh X key press (KeyX = the X
   *  key on QWERTY and AZERTY alike) or the pad's bottom face button 0 (PS ✕ /
   *  Xbox A / Switch B — padGlyphs' `confirm`; owner 2026-11-05: "the x of the
   *  ps4 controller") is CONSUMED as a dismiss: it never reaches the
   *  key set / action taps, and the pad button is masked until released, so
   *  whatever that key/button is bound to (jump on the pad by default) does
   *  not also fire. Unarmed, both behave exactly as before. */
  dismissArmed = false;
  private dismissTap = false;
  /** Pad button index masked until release after it was used to dismiss (-1 = none). */
  private padSwallow = -1;
  private prevDismissFrame = false;
  /** Read + clear the latched dismiss press (call once per frame). */
  consumeDismiss(): boolean {
    const t = this.dismissTap;
    this.dismissTap = false;
    return t;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.code === "Escape") {
      this.onEscape?.();
      return;
    }
    if (this.dismissArmed && e.code === "KeyX" && !e.repeat) {
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (!typing) {
        this.dismissTap = true;
        e.preventDefault();
        return; // consumed: never enters `keys`/`tapped`, so a binding on X stays silent
      }
    }
    // Alt is the melee key — without preventDefault the browser eats it
    // (menu-bar focus) and the stab never registers
    if (e.code === "AltLeft" || e.code === "AltRight") e.preventDefault();
    // Mic toggle handled here with a time debounce (NOT via the keys-set edge):
    // toggling exits pointer lock, and the permission prompt can steal the
    // keyup, which would leave "KeyM" stuck in the set and block later presses.
    // match e.key too: e.code is the PHYSICAL QWERTY position, so on AZERTY
    // the key labeled M reports code "Semicolon" and never triggered the mic
    if (e.code === "KeyM" || e.key === "m" || e.key === "M") {
      const t = performance.now();
      if (t - this.lastMicTap > 350) { this.lastMicTap = t; this.onMicKey?.(); }
      return;
    }
    if (e.code === "Tab" || e.code === "F1") e.preventDefault(); // F1 = help, not the browser's
    if (e.code === "ArrowUp") {
      e.preventDefault();
      if (!this.keys.has(e.code)) this.healTap = true;
    }
    if (e.code === "Digit1" || e.code === "Numpad1") {
      e.preventDefault();
      if (!this.keys.has(e.code)) this.healTap = true;
    }
    if (e.code === "Digit2" || e.code === "Numpad2") {
      e.preventDefault();
      if (!this.keys.has(e.code)) this.grenadeTap = true;
    }
    if (e.code === "ArrowDown") {
      // ArrowDown now OPENS the emote wheel (release picks). Only while actually
      // playing (pointer locked) so it never fires while typing in chat / menus.
      if (document.pointerLockElement !== null) {
        e.preventDefault();
        if (!this.keys.has(e.code)) this.openEmoteWheel();
      }
    }
    if (!this.keys.has(e.code)) {
      for (const [action, codes] of Object.entries(this.bindings)) {
        if (codes.includes(e.code)) this.tapped.add(action as Action);
      }
      if (this.bindings.melee.includes(e.code)) this.meleeTap = true; // quick stab
      if (this.bindings.grenade.includes(e.code)) this.grenadeTap = true; // throw grenade
      if (this.bindings.stoke.includes(e.code)) this.stokeTap = true; // BURNOUT wager
      if (!this.behavior.crouchHold && this.bindings.crouch.includes(e.code)) {
        this.crouchToggled = !this.crouchToggled;
      }
      // double-tap forward within 280ms → engage run until forward released
      if (this.bindings.forward.includes(e.code)) {
        const t = performance.now();
        if (t - this.lastFwdTapMs < 280) this.runMode = true;
        this.lastFwdTapMs = t;
      }
    }
    this.keys.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
    // releasing ArrowDown commits the highlighted emote (no-op if never opened)
    if (e.code === "ArrowDown") this.releaseEmoteWheel();
    // releasing forward cancels double-tap run
    if (this.bindings.forward.includes(e.code)) this.runMode = false;
  };

  /** Wall time of the last deliberate look input (mouse / right stick) —
   *  drives the vehicle free-aim (aim with the stick, no button needed). */
  lastLookAt = 0;

  // ---- TOUCH (mobile beta): third input source, merged like the gamepad ----
  touchMoveX = 0;
  touchMoveZ = 0;
  touchButtons = 0;
  touchActive = false;
  touchTap(action: "grenade" | "melee" | "heal" | "fire" | "stoke"): void {
    if (action === "grenade") this.grenadeTap = true;
    else if (action === "melee") this.meleeTap = true;
    else if (action === "heal") this.healTap = true;
    else if (action === "stoke") this.stokeTap = true;
    else this.tapped.add("fire");
  }

  /** Running |movementX|+|movementY| per event — the spike filter's baseline. */
  private lookMagEma = 0;

  private onMouseMove = (e: MouseEvent): void => {
    if (document.pointerLockElement === null) return;
    if (this.emoteWheelOpen) {
      // steer the wheel, NOT the camera — freeze look while choosing an emote
      const C = 170; // clamp so the highlight stays snappy on wild drags
      this.emoteVecX = Math.max(-C, Math.min(C, this.emoteVecX + e.movementX));
      this.emoteVecY = Math.max(-C, Math.min(C, this.emoteVecY + e.movementY));
      this.emoteSel = this.wheelSelect();
      return;
    }
    // SPIKE FILTER: Chromium on Windows (high-polling mice especially, and the
    // first events after pointer-lock is (re)acquired) intermittently reports a
    // bogus movementX/Y of hundreds of px in ONE event. Applied raw, that
    // slams the pitch into the floor clamp and the alternating junk reads as
    // the view "pointing at the floor and shaking". Real motion is continuous,
    // so drop a lone event far outside the recent running magnitude.
    const mag = Math.abs(e.movementX) + Math.abs(e.movementY);
    const spike = mag > 220 && mag > this.lookMagEma * 6 + 80;
    this.lookMagEma += (Math.min(mag, 400) - this.lookMagEma) * 0.25;
    if (spike) return;
    if (mag > 1) this.lastLookAt = performance.now();
    const s = this.sens.mouse * (this.adsHeld ? this.sens.adsMult : 1) * this.zoomMult;
    this.yaw -= e.movementX * s;
    const dy = e.movementY * s * (this.sens.invertY ? -1 : 1);
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch - dy));
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (document.pointerLockElement === null) return;
    this.mouseButtons |= 1 << e.button;
    if (e.button === 0) this.tapped.add("fire");
    if (e.button === 1) {
      // middle mouse (wheel click) = quick knife stab, no weapon swap
      e.preventDefault();
      this.meleeTap = true;
    }
    if (e.button === 2 && !this.behavior.adsHold) this.adsToggled = !this.adsToggled;
  };

  private onMouseUp = (e: MouseEvent): void => {
    this.mouseButtons &= ~(1 << e.button);
  };

  /** Forget held mouse buttons + queued taps (called on local death so a
   *  fire button held while dying can't carry into the respawn and both waste
   *  a shot AND forfeit the spawn immunity). */
  releaseButtons(): void {
    this.mouseButtons = 0;
    this.tapped.clear();
  }

  /** Open the emote wheel (ArrowDown press-edge while playing). */
  private openEmoteWheel(): void {
    if (this.emoteWheelOpen) return;
    this.emoteWheelOpen = true;
    this.emoteVecX = 0;
    this.emoteVecY = 0;
    this.emoteSel = -1;
  }
  /** Release the wheel (ArrowDown key-up): latch the highlighted emote, or the
   *  last pick when the player never left the centre deadzone (quick tap). */
  private releaseEmoteWheel(): void {
    if (!this.emoteWheelOpen) return;
    this.emoteWheelOpen = false;
    const deliberate = this.emoteSel >= 0; // steered to a tile vs quick tap
    const kind = deliberate ? this.emoteSel : this.lastEmoteKind;
    // A deliberate steer sets the new favourite (persisted); a quick tap just
    // repeats the current favourite, so there is nothing new to store.
    if (deliberate && kind !== this.lastEmoteKind) saveFavEmote(kind);
    this.lastEmoteKind = kind;
    this.emotePick = kind;
    this.emoteSel = -1;
  }
  /** Abandon the wheel with NO pick (focus loss / lost pointer lock). */
  cancelEmoteWheel(): void {
    this.emoteWheelOpen = false;
    this.emoteSel = -1;
  }
  /** Read + clear the committed emote pick (-1 = none this frame). */
  consumeEmotePick(): number {
    const k = this.emotePick;
    this.emotePick = -1;
    return k;
  }
  /** Map the accumulated pointer vector to a segment (0..count-1), or -1 inside
   *  the centre deadzone. Segment 0 sits at the top; indices increase clockwise
   *  — the same layout hud.ts lays the wheel out in. */
  private wheelSelect(): number {
    const x = this.emoteVecX, y = this.emoteVecY;
    if (x * x + y * y < 34 * 34) return -1; // centre deadzone (px)
    let ang = Math.atan2(x, -y); // 0 = up, clockwise positive
    if (ang < 0) ang += Math.PI * 2;
    return Math.round(ang / ((Math.PI * 2) / this.emoteCount)) % this.emoteCount;
  }

  attach(): void {
    window.addEventListener("np-padbinds", this.padBindsListener);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("blur", this.onWindowBlur);
    this.gpEdgeTimer ??= setInterval(this.pollGamepadEdges, 8); // ~125 Hz
  }

  /** Losing window focus eats the keyup events — clear all held input so keys
   *  pressed while alt-tabbing away can't stay stuck when focus returns. */
  private onWindowBlur = (): void => {
    this.keys.clear();
    this.mouseButtons = 0;
    this.tapped.clear();
    this.runMode = false;
    this.cancelEmoteWheel(); // don't leave the wheel stuck open on alt-tab
  };

  detach(): void {
    window.removeEventListener("np-padbinds", this.padBindsListener);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("blur", this.onWindowBlur);
    if (this.gpEdgeTimer) { clearInterval(this.gpEdgeTimer); this.gpEdgeTimer = null; }
    this.keys.clear();
    this.mouseButtons = 0;
  }

  private held(action: Action): boolean {
    return this.bindings[action].some((c) => this.keys.has(c));
  }

  /** Consume an edge-triggered tap (returns true at most once per press). */
  consumeTap(action: Action | "fire"): boolean {
    return this.tapped.delete(action);
  }

  clearTaps(): void {
    this.tapped.clear();
  }

  // ---- Xbox / gamepad (COD-style layout) ----
  private gpMoveX = 0;
  private gpMoveZ = 0;
  private gpButtons = 0;
  private gpAds = false;
  private gpFreeLook = false;
  private gpLookX = 0;
  private gpLookY = 0;
  private lastGamepadUseAt = 0;
  /** True only when the connected pad was actually used recently; a ghost pad
   *  being present must not change mouse/keyboard camera behavior. */
  get usingGamepad(): boolean { return performance.now() - this.lastGamepadUseAt < 2000; }
  /** Extra look-sensitivity multiplier while zoomed (sniper scope sets ~0.22). */
  zoomMult = 1;

  /** Drop toggled aim (called on death so you never respawn scoped). */
  resetAds(): void {
    this.adsToggled = false;
  }

  /** Poll the gamepad once per rendered frame; sticks drive move + look,
   *  buttons map to the action bitmask. Standard mapping:
   *  LS move · RS look · RT fire · LT aim · A jump · B crouch · X reload ·
   *  Y use · RB grenade · LB grapple (vehicle descend) · L3 sprint ·
   *  R3 melee · Start pause. */
  /** Live pad diagnostic for the HUD: which pad the GAME picked + what it
   *  currently reads (buttons pressed / stick deflection). */
  padDebug(): string {
    const gp = activeGamepad();
    if (!gp) return "pad:none";
    const pressed = gp.buttons.map((b, i) => (b.pressed ? i : -1)).filter((i) => i >= 0);
    const ax = Math.max(Math.abs(gp.axes[0] ?? 0), Math.abs(gp.axes[1] ?? 0));
    const rs = rightStick(gp);
    return `pad:${gp.mapping || "raw"} b[${pressed.join(",")}] LS${ax.toFixed(1)} RS${rs.x.toFixed(2)},${rs.y.toFixed(2)}`;
  }

  updateGamepad(dt: number): void {
    const gp = activeGamepad();
    if (!gp) { this.gpMoveX = this.gpMoveZ = 0; this.gpButtons = 0; this.gpAds = this.gpFreeLook = false; return; }

    const dz = (v: number): number => (Math.abs(v) < 0.18 ? 0 : (v - Math.sign(v) * 0.18) / 0.82);
    this.gpMoveX = dz(gp.axes[0] ?? 0);
    this.gpMoveZ = -dz(gp.axes[1] ?? 0); // stick up = forward
    if (gp.buttons.some((b) => b.pressed || b.value > 0.35) || gp.axes.some((v) => Math.abs(v) > 0.28)) {
      this.lastGamepadUseAt = performance.now();
    }

    const pb = this.padBinds; // rebindable (Settings → Controller)
    if (!this.driving) {
      // Analog triggers can oscillate around the browser's `pressed` cutoff.
      // Separate enter/leave thresholds stop ADS/FOV from flickering while a
      // trigger is held near its actuation point (common through Steam Input).
      const adsButton = gp.buttons[pb.ads];
      const adsValue = adsButton?.value ?? (adsButton?.pressed ? 1 : 0);
      this.gpAds = this.gpAds
        ? !!adsButton?.pressed || adsValue > 0.18
        : !!adsButton?.pressed || adsValue > 0.32;
    }

    // right stick look — fast base turn speed (rad/s) scaled by the sens
    // slider; a mild curve keeps fine aim near center without feeling sluggish.
    const rs = rightStick(gp);
    // A radial deadzone has no axis-boundary discontinuity: a noisy diagonal
    // stick cannot alternate X/Y movement as each component crosses its own
    // threshold. 24% is deliberately above typical worn-stick drift.
    const rmag = Math.hypot(rs.x, rs.y);
    const rDead = 0.24;
    const rScale = rmag > rDead ? Math.min(1, (rmag - rDead) / (1 - rDead)) / Math.max(rmag, 1e-6) : 0;
    const rawRx = rs.x * rScale;
    const rawRy = rs.y * rScale;
    // Filter every controller-look sample, not only ADS. The exponential form
    // is refresh-rate independent; ~30ms hip / ~45ms ADS settles ADC noise but
    // remains much faster than human reaction time.
    const filterHz = this.gpAds ? 22 : 32;
    const k = 1 - Math.exp(-Math.max(0, dt) * filterHz);
    this.gpLookX += (rawRx - this.gpLookX) * k;
    this.gpLookY += (rawRy - this.gpLookY) * k;
    const rx = this.gpLookX, ry = this.gpLookY;
    if (Math.abs(rx) > 0.15 || Math.abs(ry) > 0.15) this.lastLookAt = performance.now();
    // aiming: cut the turn speed hard for precise aim (min of the setting + 0.45)
    const adsMul = this.gpAds ? Math.min(0.45, this.sens.adsMult) : 1;
    const speed = (4.0 + this.sens.mouse * 500) * adsMul * this.zoomMult; // ~4.8–6+ rad/s at full tilt
    const curve = (v: number): number => Math.sign(v) * Math.pow(Math.abs(v), 1.35);
    this.yaw -= curve(rx) * speed * dt;
    const dpitch = curve(ry) * speed * dt * (this.sens.invertY ? -1 : 1);
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch - dpitch));

    // DISMISS: frame-rate edge too (the 125 Hz poll usually wins the race, but
    // a frame sampled first must not leak one frame of the bound action)
    const rawDismiss = !!gp.buttons[0]?.pressed;
    if (this.padSwallow >= 0 && !gp.buttons[this.padSwallow]?.pressed) this.padSwallow = -1;
    if (this.dismissArmed && rawDismiss && !this.prevDismissFrame) { this.dismissTap = true; this.padSwallow = 0; }
    this.prevDismissFrame = rawDismiss;
    const btn = (i: number): boolean => i !== this.padSwallow && !!gp!.buttons[i]?.pressed;
    let b = 0;
    // some DirectInput pads DOUBLE-REPORT a shoulder press on two indexes
    // (R1 also tripping the fire index) — in a ground vehicle, a fresh hop
    // press mutes pad-fire for a beat so the hop never lobs a bomb
    const groundHopHeld = this.driving && this.drivingGround && btn(5);
    if (groundHopHeld) this.padFireMuteUntil = performance.now() + 220;
    const fireMuted = this.driving && this.drivingGround && (groundHopHeld || performance.now() < this.padFireMuteUntil);
    if (btn(pb.fire) && !fireMuted) b |= Buttons.Fire;
    // ON FOOT only: in a vehicle the jump button (X) must not fire the kart
    // hop — driving jump lives on L2 (climb) and R1/Space (ground hop)
    if (!this.driving && btn(pb.jump)) { b |= Buttons.Jump; this.crouchToggled = false; } // jump cancels toggled crouch
    // crouch hold mode — toggle mode is edge-detected in the fast poll below
    if (this.behavior.crouchHold && btn(pb.crouch)) b |= Buttons.Crouch;
    if (btn(pb.sprint)) b |= Buttons.Sprint;
    if (btn(pb.reload)) b |= Buttons.Reload;
    if (btn(pb.use)) b |= Buttons.Use; // interact / enter vehicle
    if (this.driving) {
      // heli/kart controls stay FIXED: LT (L2) = ascend, LB (L1) = descend,
      // RB = aimed rockets (free-look). Keyboard: Space up, C down, Shift aimed.
      if (btn(6)) b |= Buttons.Jump;
      // GROUND rides (karts): R1 also JUMPS (hop over bombs / hop-drift);
      // Space does the same on keyboard via the normal jump binding
      if (this.drivingGround && btn(5)) b |= Buttons.Jump;
      if (btn(4)) b |= Buttons.Crouch;
      this.gpFreeLook = btn(5);
      this.gpAds = false;
    } else {
      // L1/LB is the on-foot grapple. Vehicles retain their fixed L1 descend
      // control above, so the hook can never activate while driving.
      if (btn(4)) b |= Buttons.Grapple;
      this.gpFreeLook = false;
    }
    this.gpButtons = b;
  }

  /** Rebindable pad buttons — kept fresh via the np-padbinds event. */
  private padBinds = loadPadBinds();
  private padBindsListener = (): void => { this.padBinds = loadPadBinds(); };

  /** Set by the match each frame — remaps triggers to flight controls. */
  driving = false;
  /** TRUE when the current ride is a ground vehicle (kart class) — R1 jumps. */
  drivingGround = false;
  private padFireMuteUntil = 0;

  /** FAST gamepad edge poll (125 Hz, its own timer — NOT the render loop).
   *  Pad buttons are polled, not evented: at low fps a quick R3/RT/RB click
   *  can press AND release between two rendered frames and vanish. Latching
   *  edges on a fixed fast timer makes every click land ("laggy knife" fix).
   *  Held state and sticks stay in updateGamepad (they need frame dt). */
  private prevGpFast: boolean[] = [];
  private prevActPressed: Record<string, boolean> = {};
  private fastPadIndex = -1;
  private gpEdgeTimer: ReturnType<typeof setInterval> | null = null;

  private pollGamepadEdges = (): void => {
    const gp = activeGamepad();
    if (!gp) {
      this.prevGpFast = [];
      this.prevActPressed = {};
      this.fastPadIndex = -1;
      return;
    }
    if (gp.index !== this.fastPadIndex) {
      this.fastPadIndex = gp.index;
      this.prevGpFast = [];
      this.prevActPressed = {};
    }
    if (this.padSwallow >= 0 && !gp.buttons[this.padSwallow]?.pressed) this.padSwallow = -1;
    if (this.dismissArmed && gp.buttons[0]?.pressed && !this.prevGpFast[0]) { this.dismissTap = true; this.padSwallow = 0; }
    const btn = (i: number): boolean => i !== this.padSwallow && !!gp!.buttons[i]?.pressed;
    const edge = (i: number): boolean => btn(i) && !this.prevGpFast[i];
    // Many pads (incl. the user's) report the D-pad as a HAT axis (axes[9],
    // 8-way encoded) instead of buttons 12-15, so buttons(12/13) never fire and
    // in-game D-pad taunt/heal were dead. Fold the hat in (same decode as the
    // menu nav in main.ts: step 0..7 clockwise from up).
    const hat = gp.axes[9] ?? 3.3; // ≈3.3 when released
    const step = hat > 2 ? -1 : Math.round((hat + 1) / (2 / 7));
    const hatUp = step === 7 || step === 0 || step === 1;
    const hatRight = step >= 1 && step <= 3;
    const hatDown = step >= 3 && step <= 5;
    const hatLeft = step >= 5 && step <= 7;
    // any bind on 12-15 also honors the hat encoding
    const pressedIdx = (i: number): boolean =>
      i === 12 ? (btn(12) || hatUp)
      : i === 13 ? (btn(13) || hatDown)
      : i === 14 ? (btn(14) || hatLeft)
      : i === 15 ? (btn(15) || hatRight)
      : btn(i);
    const pb = this.padBinds;
    const actEdge = (name: string, i: number): boolean => {
      const nowP = pressedIdx(i);
      const e = nowP && !this.prevActPressed[name];
      this.prevActPressed[name] = nowP;
      return e;
    };
    if (actEdge("fire", pb.fire) &&
        !(this.driving && this.drivingGround && performance.now() < this.padFireMuteUntil)) {
      this.tapped.add("fire"); // quick pulls always fire once
    }
    if (actEdge("melee", pb.melee)) this.meleeTap = true;
    // in a GROUND vehicle R1 is the HOP — it must not also lob a grenade/bomb
    // (helis keep their grenade drops)
    if (actEdge("grenade", pb.grenade) && !(this.driving && this.drivingGround)) this.grenadeTap = true;
    if (actEdge("heal", pb.heal)) this.healTap = true;
    if (actEdge("taunt", pb.taunt)) this.tauntTap = true;
    if (actEdge("stoke", pb.stoke)) this.stokeTap = true;
    if (!this.behavior.crouchHold && actEdge("crouch", pb.crouch)) this.crouchToggled = !this.crouchToggled;
    if (edge(9)) this.onEscape?.();       // Start — pause (fixed)
    if (edge(8)) this.onMicKey?.();       // Back/View — mic toggle (fixed)
    this.prevGpFast = gp.buttons.map((x) => x.pressed);
  };

  sample(): { moveX: number; moveZ: number; buttons: number } {
    let moveX = 0;
    let moveZ = 0;
    if (this.held("forward")) moveZ += 1;
    if (this.held("back")) moveZ -= 1;
    if (this.held("right")) moveX += 1;
    if (this.held("left")) moveX -= 1;
    // gamepad left stick overrides when deflected (analog)
    if (this.gpMoveX !== 0) moveX = this.gpMoveX;
    if (this.gpMoveZ !== 0) moveZ = this.gpMoveZ;
    // touch stick (mobile beta) overrides likewise
    if (this.touchMoveX !== 0) moveX = this.touchMoveX;
    if (this.touchMoveZ !== 0) moveZ = this.touchMoveZ;

    let buttons = this.gpButtons | this.touchButtons; // pad + touch held actions
    // held mask OR the press latch: on a slow frame a quick click can press
    // AND release before the next sample — the latch guarantees every click
    // still yields at least one Fire input (fixes "late or eaten" triggers)
    if ((this.mouseButtons & 1) !== 0 || this.tapped.delete("fire")) buttons |= Buttons.Fire;
    this.adsHeld = (this.behavior.adsHold ? (this.mouseButtons & 4) !== 0 : this.adsToggled) || this.gpAds ||
      (this.touchButtons & Buttons.ADS) !== 0;
    if (this.adsHeld) buttons |= Buttons.ADS;
    // held OR the press latch — same fix Fire got: on a slow frame a quick
    // Space tap can press AND release before the next sample, eating the
    // jump entirely. Rarely felt at 60 fps (a tap spans several frames) but
    // at degraded frame rates it reads as "jump randomly doesn't respond".
    if (this.held("jump") || this.tapped.delete("jump")) {
      buttons |= Buttons.Jump;
      this.crouchToggled = false; // jump cancels toggled crouch
    }
    const crouch = this.behavior.crouchHold ? this.held("crouch") : this.crouchToggled;
    if (crouch) buttons |= Buttons.Crouch;
    // sprint: dedicated key, OR double-tap-forward run while moving forward
    if (this.held("sprint") || (this.runMode && this.held("forward"))) {
      buttons |= Buttons.Sprint;
    }
    if (this.held("reload")) buttons |= Buttons.Reload;
    // quick melee: a single-frame Knife press (consumed here so it's one stab)
    if (this.grenadeTap) {
      buttons |= Buttons.Grenade;
      this.grenadeTap = false;
    }
    if (this.meleeTap) {
      buttons |= Buttons.Knife;
      this.meleeTap = false;
    }
    if (this.healTap) {
      buttons |= Buttons.Heal;
      this.healTap = false;
    }
    if (this.tauntTap) {
      buttons |= Buttons.Taunt;
      this.tauntTap = false;
    }
    if (this.stokeTap) {
      buttons |= Buttons.Stoke;
      this.stokeTap = false;
    }
    if (this.held("use")) buttons |= Buttons.Use;
    // GRAPPLE: held (G on keyboard, 🪝 on touch) — fires + reels while down
    if (this.held("grapple") || (this.touchButtons & Buttons.Grapple) !== 0) buttons |= Buttons.Grapple;
    return { moveX, moveZ, buttons };
  }

  /** Hold H / F1 → controls + colour legend overlay (onboarding.ts). */
  get helpHeld(): boolean {
    return this.keys.has("KeyH") || this.keys.has("F1");
  }

  get scoreboardHeld(): boolean {
    return this.held("scoreboard");
  }

  /** HOLD-to-third-person: the bound key (default V) OR the PS4/standard D-pad →
   *  (button 15, otherwise unassigned). View-only, so it lives beside
   *  scoreboardHeld rather than in the gpButtons game-input bitmask. */
  get thirdPersonHeld(): boolean {
    return this.held("thirdPerson") || (activeGamepad()?.buttons[15]?.pressed ?? false);
  }
}
